/*
  My Garage and the vehicle selector.

  The garage lives in localStorage rather than behind a login, because a shopper
  who has to create an account before they can say what they drive will simply
  leave. A logged-in customer's garage is written to their account metafield by
  the server when they are signed in; this file is the storefront half.

  Nothing here asserts that a part fits. The data comes from supplier titles, so
  the strongest claim the UI is allowed to make is what the supplier listed, and
  the shopper is told when a model year was never stated.
*/

(() => {
  // Several snippets need this file and each asks for it, so the same src can
  // appear more than once on a page. Running twice would double every listener.
  if (window.APHGarage) return;

  const STORAGE_KEY = 'aph:garage:v1';
  const MAX_VEHICLES = 8;
  const FIRST_YEAR = 1985;

  const index = window.APH_VEHICLE_INDEX || { makes: {} };

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      return { vehicles: Array.isArray(raw.vehicles) ? raw.vehicles : [], activeId: raw.activeId || null };
    } catch (error) {
      return { vehicles: [], activeId: null };
    }
  };

  const write = (state) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      /* Private browsing: the selector still works for this page view. */
    }
    document.dispatchEvent(new CustomEvent('aph:garage:change', { detail: state }));
  };

  const label = (vehicle) => [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
  const vehicleId = (vehicle) => `${vehicle.year || ''}|${vehicle.make}|${vehicle.model || ''}`;

  const garage = {
    state: read(),

    get active() {
      return this.state.vehicles.find((vehicle) => vehicleId(vehicle) === this.state.activeId) || null;
    },

    add(vehicle) {
      const id = vehicleId(vehicle);
      const existing = this.state.vehicles.filter((entry) => vehicleId(entry) !== id);
      this.state.vehicles = [{ ...vehicle }, ...existing].slice(0, MAX_VEHICLES);
      this.state.activeId = id;
      write(this.state);
    },

    remove(id) {
      this.state.vehicles = this.state.vehicles.filter((vehicle) => vehicleId(vehicle) !== id);
      if (this.state.activeId === id) {
        this.state.activeId = this.state.vehicles.length ? vehicleId(this.state.vehicles[0]) : null;
      }
      write(this.state);
    },

    select(id) {
      this.state.activeId = id;
      write(this.state);
    },

    clearActive() {
      this.state.activeId = null;
      write(this.state);
    },
  };

  // --- selector ------------------------------------------------------------

  const makes = () => Object.keys(index.makes || {}).sort();

  const modelsFor = (make) => {
    const models = (index.makes || {})[make] || {};
    return Object.keys(models).sort((a, b) => models[b].products - models[a].products);
  };

  const yearsFor = (make, model) => {
    const entry = ((index.makes || {})[make] || {})[model];
    const now = new Date().getFullYear() + 1;
    if (!entry) return [];
    const from = entry.year_from || FIRST_YEAR;
    const to = entry.year_to || now;
    const years = [];
    for (let year = Math.min(to, now); year >= from; year -= 1) years.push(year);
    return years;
  };

  const fill = (select, values, placeholder) => {
    select.innerHTML = '';
    const first = document.createElement('option');
    first.value = '';
    first.textContent = placeholder;
    select.appendChild(first);
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });
    select.disabled = values.length === 0;
  };

  class VehicleSelector extends HTMLElement {
    connectedCallback() {
      this.makeField = this.querySelector('[data-field="make"]');
      this.modelField = this.querySelector('[data-field="model"]');
      this.yearField = this.querySelector('[data-field="year"]');
      this.submit = this.querySelector('[data-action="find"]');
      if (!this.makeField) return;

      fill(this.makeField, makes(), this.dataset.makeLabel || 'Select make');
      fill(this.modelField, [], this.dataset.modelLabel || 'Select model');
      fill(this.yearField, [], this.dataset.yearLabel || 'Select year');

      this.makeField.addEventListener('change', () => {
        fill(this.modelField, modelsFor(this.makeField.value), this.dataset.modelLabel || 'Select model');
        fill(this.yearField, [], this.dataset.yearLabel || 'Select year');
        this.refresh();
      });
      this.modelField.addEventListener('change', () => {
        fill(
          this.yearField,
          yearsFor(this.makeField.value, this.modelField.value),
          this.dataset.yearLabel || 'Select year'
        );
        this.refresh();
      });
      this.yearField.addEventListener('change', () => this.refresh());
      this.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });
      this.refresh();
    }

    refresh() {
      // A make alone is enough to browse; the model is what makes it useful.
      if (this.submit) this.submit.disabled = !this.makeField.value;
    }

    apply() {
      const vehicle = {
        make: this.makeField.value,
        model: this.modelField.value || null,
        year: this.yearField.value ? Number(this.yearField.value) : null,
      };
      if (!vehicle.make) return;
      garage.add(vehicle);
      if (this.dataset.redirect === 'false') return;
      window.location.href = searchUrl(vehicle);
    }
  }

  const searchUrl = (vehicle, extra) => {
    const terms = [vehicle.make, vehicle.model, extra].filter(Boolean).join(' ');
    const params = new URLSearchParams({ q: terms, type: 'product' });
    // Passing the metafield facet as well means the results narrow properly the
    // moment these filters are switched on in Search & Discovery, and is ignored
    // until then rather than breaking the URL.
    if (vehicle.model) params.append('filter.p.m.custom.vehicle_models', vehicle.model);
    if (vehicle.make) params.append('filter.p.m.custom.vehicle_makes', vehicle.make);
    return `${window.Shopify?.routes?.root || '/'}search?${params.toString()}`;
  };

  // --- active vehicle bar --------------------------------------------------

  const renderBar = () => {
    document.querySelectorAll('[data-garage-bar]').forEach((bar) => {
      const active = garage.active;
      const nameSlot = bar.querySelector('[data-garage-name]');
      bar.hidden = !active;
      if (active && nameSlot) nameSlot.textContent = label(active);
      const shopLink = bar.querySelector('[data-garage-shop]');
      if (active && shopLink) shopLink.href = searchUrl(active);
    });

    document.querySelectorAll('[data-garage-list]').forEach((list) => {
      const active = garage.active;
      if (!garage.state.vehicles.length) {
        list.innerHTML = `<li class="garage__empty">${list.dataset.emptyText || 'No saved vehicles yet.'}</li>`;
        return;
      }
      list.innerHTML = garage.state.vehicles
        .map((vehicle) => {
          const id = vehicleId(vehicle);
          const current = active && vehicleId(active) === id;
          return `
            <li class="garage__item${current ? ' garage__item--active' : ''}">
              <button type="button" class="garage__select" data-garage-select="${encodeURIComponent(id)}">
                ${label(vehicle)}${current ? ' <span class="garage__badge">Active</span>' : ''}
              </button>
              <button type="button" class="garage__remove" data-garage-remove="${encodeURIComponent(id)}" aria-label="Remove ${label(vehicle)}">&times;</button>
            </li>`;
        })
        .join('');
    });
  };

  document.addEventListener('click', (event) => {
    const select = event.target.closest('[data-garage-select]');
    if (select) {
      garage.select(decodeURIComponent(select.dataset.garageSelect));
      return;
    }
    const remove = event.target.closest('[data-garage-remove]');
    if (remove) {
      garage.remove(decodeURIComponent(remove.dataset.garageRemove));
      return;
    }
    if (event.target.closest('[data-garage-clear]')) garage.clearActive();
  });

  // --- fitment verdict on a product page ----------------------------------

  const verdict = (fitment, vehicle) => {
    const makesListed = (fitment.makes || []).map((value) => value.toLowerCase());
    const modelsListed = (fitment.models || []).map((value) => value.toLowerCase());
    if (!makesListed.length && !modelsListed.length) {
      return { level: 'unknown', text: 'The supplier does not list vehicles for this part. Check the part number against your car.' };
    }
    if (!makesListed.includes(vehicle.make.toLowerCase())) {
      return {
        level: 'no',
        text: `Listed for ${(fitment.makes || []).join(', ')} — not for your ${label(vehicle)}.`,
      };
    }
    if (vehicle.model && modelsListed.length && !modelsListed.includes(vehicle.model.toLowerCase())) {
      return {
        level: 'check',
        text: `Listed for ${vehicle.make} ${(fitment.models || []).join(', ')}. Your ${vehicle.model} is not named, so check the part number before ordering.`,
      };
    }
    const from = fitment.year_from;
    const to = fitment.year_to;
    if (vehicle.year && from && ((to && vehicle.year > to) || vehicle.year < from)) {
      const window_ = to ? `${from}\u2013${to}` : `${from} onwards`;
      return {
        level: 'no',
        text: `Listed for ${window_} models. Your ${vehicle.year} is outside that range.`,
      };
    }
    if (!from) {
      return {
        level: 'check',
        text: `Listed for the ${vehicle.make}${vehicle.model ? ` ${vehicle.model}` : ''}. The supplier did not state model years, so confirm the part number for your ${vehicle.year || 'model year'}.`,
      };
    }
    return {
      level: 'yes',
      text: `Listed as fitting your ${label(vehicle)}${to ? ` (${from}\u2013${to} models)` : ` (${from} onwards)`}.`,
    };
  };

  const renderFitment = () => {
    document.querySelectorAll('[data-fitment]').forEach((node) => {
      let fitment;
      try {
        fitment = JSON.parse(node.dataset.fitment || '{}');
      } catch (error) {
        return;
      }
      const active = garage.active;
      const prompt = node.querySelector('[data-fitment-prompt]');
      const result = node.querySelector('[data-fitment-result]');
      if (!result) return;
      if (!active) {
        if (prompt) prompt.hidden = false;
        result.hidden = true;
        return;
      }
      const outcome = verdict(fitment, active);
      if (prompt) prompt.hidden = true;
      result.hidden = false;
      result.className = `fitment__result fitment__result--${outcome.level}`;
      result.textContent = outcome.text;
    });
  };

  // --- narrowing a category to the saved vehicle ---------------------------

  const renderNarrow = () => {
    document.querySelectorAll('[data-garage-narrow]').forEach((node) => {
      const active = garage.active;
      node.hidden = !active;
      if (!active) return;
      const context = node.dataset.garageNarrow || '';
      const link = node.querySelector('a');
      const slot = node.querySelector('[data-garage-narrow-name]');
      if (slot) slot.textContent = label(active);
      if (link) link.href = searchUrl(active, context);
    });
  };

  const renderAll = () => {
    renderBar();
    renderFitment();
    renderNarrow();
  };

  document.addEventListener('aph:garage:change', renderAll);
  document.addEventListener('DOMContentLoaded', renderAll);
  if (document.readyState !== 'loading') renderAll();

  if (!window.customElements.get('vehicle-selector')) {
    window.customElements.define('vehicle-selector', VehicleSelector);
  }
  window.APHGarage = { garage, searchUrl, label };
})();
